import { NavLink } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Target,
  CalendarClock,
  ArrowLeft,
  Sparkles
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';

interface ManagerNavItemProps {
  to: string;
  icon: React.ReactNode;
  label: string;
  end?: boolean;
}

function ManagerNavItem({ to, icon, label, end = false }: ManagerNavItemProps) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200',
          isActive
            ? 'bg-primary text-primary-foreground shadow-md shadow-primary/20'
            : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
        )
      }
    >
      {icon}
      <span className="flex-1">{label}</span>
    </NavLink>
  );
}

export function ManagerSidebar() {
  const { user } = useAuth();

  return (
    <aside className="fixed inset-y-0 left-0 z-40 w-64 bg-card border-r border-border flex flex-col transition-all duration-200">
      {/* Header */}
      <div className="h-16 px-4 flex items-center justify-between border-b border-border">
        <div className="flex items-center gap-2">
          <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center shadow-lg">
            <Sparkles className="h-4 w-4 text-white" />
          </div>
          <div className="flex flex-col">
            <span className="font-bold text-foreground text-sm">Manager Console</span>
            <span className="text-xs text-muted-foreground">CEE Sales</span>
          </div>
        </div>
      </div>

      {/* Back to Sales */}
      <div className="p-3 border-b border-border">
        <NavLink
          to="/"
          className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          <span>Retour à la plateforme</span>
        </NavLink>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        <div className="pb-2">
          <span className="px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            Mon équipe
          </span>
        </div>
        
        <ManagerNavItem
          to="/admin"
          icon={<LayoutDashboard className="h-4 w-4" />}
          label="Dashboard"
          end
        />
        
        <ManagerNavItem
          to="/admin/objectives"
          icon={<Target className="h-4 w-4" />}
          label="Objectifs"
        />
        
        <ManagerNavItem
          to="/admin/planning"
          icon={<CalendarClock className="h-4 w-4" />}
          label="Planning"
        />
      </nav>

      {/* Footer */}
      <div className="p-3 border-t border-border">
        <div className="px-3 py-2 rounded-lg bg-gradient-to-r from-blue-500/10 to-indigo-500/10 border border-blue-500/20">
          <p className="text-xs text-muted-foreground">
            Manager
          </p>
          <p className="text-sm font-medium text-foreground truncate">
            {user?.name}
          </p>
        </div>
      </div>
    </aside>
  );
}
